import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Asset } from '@app/core/model/entities/asset/asset';
import { MapMarkersService } from '@app/features/main/views/assets/assets-inventory/assets-map/map-markers.service';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { CartovistaComponent } from '@app/shared/components/cartovista/cartovista.component';
import { JmapComponent } from '@app/shared/components/jmap/jmap.component';
import { LeafletComponent } from '@app/shared/components/leaflet/leaflet.component';
import { getLocale } from '@app/shared/extra/utils';
import { Filter, SelectParams } from '@app/shared/grid/datasource-utils';
import { AssetMapSource, GisService } from '@services/gis.service';
import 'leaflet-providers';
import 'leaflet-sidebar-v2';
import 'leaflet.layerscontrol-minimap';
import 'leaflet.markercluster';
import { iif, Observable, of, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'assets-map',
    templateUrl: './assets-map.component.html',
    styleUrls: ['./assets-map.component.scss'],
    providers: [
        MapMarkersService
    ],
    standalone: false
})
export class AssetsMapComponent implements OnInit {

  protected readonly AssetMapSourceEnum = AssetMapSource;

  @Input() public filters: Filter[];
  public assets: Asset[] = [];

  @ViewChild('jmapComponent') public jmapComponent: JmapComponent;
  @ViewChild('cartovistaComponent') public cartovistaComponent: CartovistaComponent;
  @ViewChild('leafletComponent') public leafletComponent: LeafletComponent;

  public assetMapSource$: Observable<AssetMapSource>;

  protected gisService = inject(GisService);
  private destroyRef = inject(DestroyRef);
  private assetService = inject(AssetsService);

  public ngOnInit(): void {
    this.assetMapSource$ = this.gisService.availableGISService()
      .pipe(
        switchMap(service => {
          const selectParams: SelectParams = {
            locale: getLocale(),
            filters: this.filters
          };

          return iif(
            () => service === AssetMapSource.NONE,
            this.assetService.loadAllAssets(selectParams)
              .pipe(
                tap(assets => { this.assets = assets; }),
                switchMap(() => of(service))
              ),
            of(service)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }

  /**
   * Open GIS map in fullscreen mode
   */
  public toggleFullScreen(): void {
    if (this.jmapComponent) {
      this.jmapComponent.jmap.nativeElement.requestFullscreen().catch(console.error);
    } else if (this.cartovistaComponent) {
      this.cartovistaComponent.cartovista.nativeElement.requestFullscreen().catch(console.error);
    } else {
      this.leafletComponent.elementRef.nativeElement.requestFullscreen().catch(console.error);
    }
  }

  /**
   * Call a refresh on the data used by the external GIS service.
   */
  public refreshData(): void {
    if (this.jmapComponent) {
      this.jmapComponent.refreshData();
    } else if (this.cartovistaComponent) {
      this.cartovistaComponent.refreshData();
    } else {
      // Do nothing for Leaflet
    }
  }
}
